import React from 'react';
import { IntlShape } from 'react-intl';

import { AccessStatusEnum } from '@/shared/models/access-status';
import { CountryCode } from '@/shared/models/country-code';
import { getBookingLink } from '@/shared/utils/bookingLinksUtils';

import { EnumCardItemStatus, ICard, ICardContext } from '../types';

import { assignTipTextForCardItems } from './assignTipTextForCardItems';
import { createBookCarAppointmentCard } from './createBookCarAppointmentCard';
import { createGBBookCarAppointmentCard } from './createGBBookCarAppointmentCard';
import { getItem } from './getItem';

const createCarsCards = (
  context: ICardContext,
  onNavigate: (uri: string) => void,
  intl: IntlShape,
  user: { isFleet: boolean },
  links: { requirementsForCar: string; bookCarInspection: string },
): ICard[] => {
  const { items, cardStatus } = getItem({
    entities: context.cars,
    docDefinitions: context.carUploadDefinitions,
    getTitle: car => `${car.licensePlate} ${car.make}`,
    getItemAction: car => () => {
      if (['pending', 'rejected', 'expired_docs'].includes(car.accessStatus)) {
        onNavigate(`vehicles/${car.id}#docs`);
      } else {
        onNavigate(`vehicles/${car.id}`);
      }
    },
  });

  const itemsWithTips = assignTipTextForCardItems(intl, items);

  const result: ICard[] = [
    {
      id: 'cars',
      isBlocked: false,
      title: (() => {
        if (context.cars.length > 1) {
          if (cardStatus === 'done') {
            return intl.formatMessage({
              defaultMessage: 'Vehicle documents approved',
              id: 'd+pK9s',
              description: 'ToDo car card: title',
            });
          }

          if (cardStatus === 'review') {
            return intl.formatMessage({
              defaultMessage: 'Vehicle documents',
              id: 'QRhVLx',
              description: 'ToDo car card: title',
            });
          }

          return intl.formatMessage({
            defaultMessage: 'Add vehicle documents',
            id: 'NCnwi4',
            description: 'ToDo car card: title',
          });
        }

        return intl.formatMessage({
          defaultMessage: 'Add vehicle',
          id: 'Z/C89n',
          description: 'ToDo car card: title',
        });
      })(),
      desc: intl.formatMessage(
        {
          defaultMessage:
            'Confirm <a>vehicle requirements.</a> The car must be inspected at the office.',
          id: 'FqgSlN',
          description: 'todo list card text',
        },
        {
          a: (text: React.ReactNode) => (
            <a href={links.requirementsForCar} target="_blank" rel="noreferrer">
              {text}
            </a>
          ),
        },
      ),
      items: itemsWithTips,
      cardStatus,
      actionTitle:
        context.cars.length === 0 || user.isFleet
          ? intl.formatMessage({
              defaultMessage: 'Add vehicle',
              id: 'EwGCyS',
              description: 'todo list card text',
            })
          : undefined,
      action: () => onNavigate(`/add-vehicle`),
    },
  ];

  if (context.partner.accessStatus === 'active') {
    if (
      context.partner.countryCode === CountryCode.GreatBritain &&
      context.myDriver &&
      context.myDriver.accessStatus !== 'pending' &&
      context.currentCar &&
      context.currentCar.accessStatus !== AccessStatusEnum.active
    ) {
      result.push(createGBBookCarAppointmentCard(intl));
    } else if (items.some(i => i.status === EnumCardItemStatus.waitingForApproval)) {
      result.push(
        createBookCarAppointmentCard(intl, {
          ...links,
          bookCarInspection: getBookingLink(links.bookCarInspection, context.myDriver),
        }),
      );
    }
  }

  return result;
};

export { createCarsCards };
